import { UploadOutlined } from "@ant-design/icons";
import { Button, message, Modal, Upload } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import config from "../Config";
import authHeader from "../services/auth-header";

const { Dragger } = Upload;
const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

const ImportDataModal = ({ open, onClose, refreshTable, formatFilePath, model }) => {
    const [file, setFile] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);

    // const allowedTypes = [
    //     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    //     "application/vnd.ms-excel", // .xls
    //     "text/csv", // .csv
    // ];
    const allowedExtensions = ['.xlsx', '.xls', '.csv'];
    const getCleanFileName = (fileUrl) => {
        // customer-type-format.e3adec572c4090485070.xlsx
        const fullName = fileUrl.split("/").pop();
      
        // customer-type-format.xlsx
        return fullName.replace(/\.[a-f0-9]{8,}(?=\.)/, "");
      };
    const uploadProps = {
        accept: ".xlsx,.xls,.csv",
        fileList,
        beforeUpload: (file) => {
            // if (!allowedTypes.includes(file.type)) {
            //     message.error("Only Excel (.xlsx, .xls) and CSV files are allowed!");
            //     return Upload.LIST_IGNORE; // stops file from being added
            // }
            const fileName = file.name.toLowerCase();
            const isAllowed = allowedExtensions.some(ext =>
                fileName.endsWith(ext)
              );
          
              if (!isAllowed) {
                message.error('Only Excel (.xlsx, .xls) and CSV files are allowed!');
                return Upload.LIST_IGNORE;
              }

            setFile(file);
            setFileList([file]); 
            return false; 
        },
        onRemove: () => {
            setFile(null);
            setFileList([]);
          },
        multiple: false,
        maxCount: 1,
    };

    useEffect(() => {
        if (open) {
            setFile(null);   
        }
    }, [open]);

    const handleImport = async () => {
        if (!file) {
            message.error("Please select a file!");
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append("model", model);
        formData.append("file", file);

        // Construct endpoint based on model
        // e.g., "item-category" -> "/item-categories/bulk-import/"
        // e.g., "department" -> "/departments/bulk-import/"
        // e.g., "temper" -> "/temper/bulk-import/" (singular)
        let endpoint = "/bulk-import/";
        if (model === "item-category") {
            endpoint = "/item-categories/bulk-import/";
        } else if (model === "department") {
            endpoint = "/departments/bulk-import/";
        } else if (model === "temper") {
            endpoint = "/temper/bulk-import/";
        } else if (model === "alloy") {
            endpoint = "/alloy/bulk-import/";
        } else if (model === "store") {
            endpoint = "/store/bulk-import/";
        } else if (model === "die-group") {
            endpoint = "/die-group/bulk-import/";
        } else if (model === "die-category") {
            endpoint = "/die-category/bulk-import/";
        } 
        else if (model === "die-subcategory") {
            endpoint = "/die-subcategory/bulk-import/";
        }
        else if (model === "die-size") {
            endpoint = "/die-size/bulk-import/";
        } else if (model === "die-press") {
            endpoint = "/die-press/bulk-import/";
        } else if (model === "packing-mode") {
            endpoint = "/packing-mode/bulk-import/";
        } else if (model) {
            // Generic: convert "model-name" to "model-names"
            endpoint = `/${model}s/bulk-import/`;
        }

        try {
            const res = await axios.post(
                `${API_URL}/api/${API_VERSION}${endpoint}`,
                formData,
                {
                    headers: {
                        ...authHeader(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            // If we reach here, HTTP status is 2xx (successful)
            // Check for success flag or import data in response
            const responseData = res.data;
            const hasSuccessFlag = responseData?.success === true;
            
            // Check for import statistics to determine if this is an import response
            let importData = null;
            if (responseData?.data && typeof responseData.data === 'object') {
                if (responseData.data.inserted !== undefined || 
                    responseData.data.updated !== undefined || 
                    responseData.data.error_count !== undefined ||
                    responseData.data.row_errors !== undefined ||
                    responseData.data.total_rows !== undefined ||
                    responseData.data.total_records !== undefined) {
                    importData = responseData.data;
                }
            } else if (responseData && typeof responseData === 'object') {
                if (responseData.inserted !== undefined || 
                    responseData.updated !== undefined || 
                    responseData.error_count !== undefined ||
                    responseData.row_errors !== undefined ||
                    responseData.total_rows !== undefined ||
                    responseData.total_records !== undefined) {
                    importData = responseData;
                }
            }

            // Show modal if we have success flag OR import data (some APIs may not set success flag)
            if (hasSuccessFlag || importData) {
                setLoading(false);
                // Close the import modal first
                onClose();
                
                if (importData) {
                    const hasErrors = (importData.error_count && importData.error_count > 0) || (importData.row_errors && importData.row_errors.length > 0);
                    const hasSuccess = (importData.inserted > 0 || importData.updated > 0 || (importData.success_count !== undefined && importData.success_count > 0));
                    const hasSkipped = importData.skipped && importData.skipped > 0;
                    const totalProcessed = importData.total_records || importData.total_rows || 0;
                    const totalFailed = importData.error_count || 0;
                    
                    // Check if API explicitly returned success: false
                    const apiSuccessFlag = responseData.success;
                    const isApiFailure = apiSuccessFlag === false;
                    
                    // Determine modal type and title based on results
                    // If API says success: false, don't show "Import Completed Successfully"
                    const isCompleteFailure = hasErrors && !hasSuccess && !hasSkipped && totalFailed === totalProcessed;
                    let modalType, modalTitle;
                    
                    if (isApiFailure) {
                        // API explicitly says success: false
                        if (hasSuccess || hasSkipped) {
                            modalType = 'warning';
                            modalTitle = 'Import Completed with Errors';
                        } else {
                            modalType = 'error';
                            modalTitle = 'Import Failed';
                        }
                    } else {
                        // API says success: true or no success flag
                        modalType = hasErrors ? (hasSuccess || hasSkipped ? 'warning' : 'error') : 'success';
                        modalTitle = isCompleteFailure 
                            ? 'Import Failed'
                            : hasErrors 
                            ? (hasSuccess || hasSkipped ? 'Import Completed with Errors' : 'Import Failed')
                            : 'Import Completed Successfully';
                    }

                    const ModalComponent = modalType === 'error' ? Modal.error : modalType === 'warning' ? Modal.warning : Modal.success;

                    // Show detailed import summary
                    ModalComponent({
                        title: modalTitle,
                        width: 800,
                        content: (
                            <div style={{ padding: '10px 0' }}>
                                {/* Backend message */}
                                {res.data.message && (
                                    <div style={{ 
                                        marginBottom: '16px', 
                                        fontSize: '16px', 
                                        fontWeight: 'normal',
                                        color: '#262626',
                                        padding: '12px',
                                        background: hasErrors ? '#fffbe6' : '#f6ffed',
                                        border: `1px solid ${hasErrors ? '#ffe58f' : '#b7eb8f'}`,
                                        borderRadius: '4px',
                                        lineHeight: '1.5'
                                    }}>
                                        {res.data.message}
                                    </div>
                                )}

                                {/* Statistics Grid */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{ padding: '8px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                                        <strong>Total Rows:</strong> {importData.total_rows || importData.total_records || 0}
                                    </div>
                                    <div style={{ padding: '8px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                                        <strong>Records Processed:</strong> {importData.total_records || importData.total_rows || 0}
                                    </div>
                                    {/* {(importData.success_count !== undefined) && (
                                        <div style={{ padding: '8px', background: '#e6fffb', border: '1px solid #87e8de', borderRadius: '4px' }}>
                                            <strong>Success:</strong> {importData.success_count}
                                        </div>
                                    )} */}
                                    {(importData.inserted !== undefined && importData.inserted > 0) && (
                                        <div style={{ padding: '8px', background: '#e6fffb', border: '1px solid #87e8de', borderRadius: '4px' }}>
                                            <strong>Inserted:</strong> {importData.inserted}
                                        </div>
                                    )}
                                    {(importData.updated !== undefined && importData.updated > 0) && (
                                        <div style={{ padding: '8px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                                            <strong>Updated:</strong> {importData.updated}
                                        </div>
                                    )}
                                    {(importData.skipped !== undefined && importData.skipped > 0) && (
                                        <div style={{ padding: '8px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px' }}>
                                            <strong>Skipped:</strong> {importData.skipped}
                                        </div>
                                    )}
                                    {(importData.error_count !== undefined && importData.error_count !== null && importData.error_count > 0) && (
                                        <div style={{ padding: '8px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                                            <strong>Errors:</strong> {importData.error_count}
                                        </div>
                                    )}
                                </div>

                                {/* Row Errors with Row Data */}
                                {importData.row_errors && importData.row_errors.length > 0 && (
                                    <div style={{ marginTop: '16px' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#ff4d4f' }}>
                                            Row Errors ({importData.row_errors.length}):
                                        </div>
                                        <div style={{
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '4px',
                                            padding: '8px'
                                        }}>
                                            {importData.row_errors.map((rowError, index) => (
                                                <div key={index} style={{
                                                    marginBottom: '12px',
                                                    padding: '12px',
                                                    background: '#fff2f0',
                                                    border: '1px solid #ffccc7',
                                                    borderRadius: '4px'
                                                }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                                                        Row {rowError.row_number} {rowError.errors && `(${rowError.errors.length} error${rowError.errors.length > 1 ? 's' : ''})`}
                                                    </div>
                                                    
                                                    {/* Row Data */}
                                                    {rowError.row_data && (
                                                        <div style={{ 
                                                            marginBottom: '8px', 
                                                            padding: '6px',
                                                            background: '#fafafa',
                                                            border: '1px solid #e8e8e8',
                                                            borderRadius: '4px',
                                                            fontSize: '12px'
                                                        }}>
                                                            <strong style={{ color: '#595959' }}>Row Data:</strong>
                                                            <div style={{ marginTop: '4px', color: '#8c8c8c' }}>
                                                                {Object.entries(rowError.row_data).map(([key, value]) => (
                                                                    <div key={key} style={{ marginLeft: '8px' }}>
                                                                        <strong>{key}:</strong> {String(value || 'N/A')}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Errors */}
                                                    {rowError.errors && rowError.errors.length > 0 && (
                                                        <div>
                                                            <strong style={{ color: '#ff4d4f', fontSize: '13px' }}>Errors:</strong>
                                                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                                                                {rowError.errors.map((error, errorIndex) => (
                                                                    <li key={errorIndex} style={{ color: '#ff4d4f', marginBottom: '4px', fontSize: '12px' }}>
                                                                        <strong>{error.field}:</strong> {error.message}
                                                                        {error.value !== undefined && (
                                                                            <span style={{ color: '#8c8c8c', marginLeft: '8px' }}>
                                                                                (Value: <em>{String(error.value)}</em>)
                                                                            </span>
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Error Summary (if exists) */}
                                {importData.error_summary && importData.error_summary.total_errors > 0 && (
                                    <div style={{ marginTop: '16px', padding: '8px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                            Error Summary:
                                        </div>
                                        <div>Total Errors: {importData.error_summary.total_errors}</div>
                                        {importData.error_summary.rows_with_errors && (
                                            <div>Rows with Errors: {importData.error_summary.rows_with_errors}</div>
                                        )}
                                        {importData.error_summary.error_types && (
                                            <div>Error Types: {Object.entries(importData.error_summary.error_types).map(([type, count]) =>
                                                `${type}: ${count}`).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ),
                        onOk() {
                            refreshTable?.();
                        },
                        okText: "Continue"
                    });
                } else {
                    // Fallback for simple success response - show modal with message
                    Modal.success({
                        title: 'Import Completed Successfully',
                        width: 600,
                        content: (
                            <div style={{ padding: '10px 0' }}>
                                {responseData.message && (
                                    <div style={{ 
                                        marginBottom: '16px', 
                                        fontSize: '16px', 
                                        fontWeight: 'normal',
                                        color: '#262626',
                                        padding: '12px',
                                        background: '#f6ffed',
                                        border: '1px solid #b7eb8f',
                                        borderRadius: '4px',
                                        lineHeight: '1.5'
                                    }}>
                                        {responseData.message}
                                    </div>
                                )}
                            </div>
                        ),
                        onOk() {
                            refreshTable?.();
                        },
                        okText: "Continue"
                    });
                }
            } else {
                // No success flag and no import data - show error
                setLoading(false);
                message.error(responseData?.message || 'Import failed');
            }        

        } catch (err) {
            
            const res = err?.response;

         
            if (
              res &&
              typeof res.data === "string" &&
              res.headers?.["content-type"]?.includes("text/html")
            ) {
              message.error("API not found or server error. Please contact admin.");
              setLoading(false);
              onClose();
              return;
            }
            
            const data = err?.response?.data;
            
            // Check if error response contains import data structure (even with success: false)
            let importData = null;
            if (data) {
                // Check nested structure (data.data)
                if (data.data && typeof data.data === 'object') {
                    if (data.data.inserted !== undefined || 
                        data.data.updated !== undefined || 
                        data.data.error_count !== undefined ||
                        data.data.row_errors !== undefined ||
                        data.data.total_rows !== undefined ||
                        data.data.total_records !== undefined ||
                        data.data.failed !== undefined) {
                        importData = data.data;
                    }
                } 
                // Check direct structure (data itself)
                else if (typeof data === 'object' && !Array.isArray(data)) {
                    if (data.inserted !== undefined || 
                        data.updated !== undefined || 
                        data.error_count !== undefined ||
                        data.row_errors !== undefined ||
                        data.total_rows !== undefined ||
                        data.total_records !== undefined ||
                        data.failed !== undefined) {
                        importData = data;
                    }
                }
            }
            
            // If import data is found, show detailed modal even for errors
            if (importData) {
                setLoading(false);
                // Close the import modal first
                onClose();
                
                const hasErrors = (importData.error_count && importData.error_count > 0) || 
                                  (importData.row_errors && importData.row_errors.length > 0) ||
                                  (importData.failed && importData.failed > 0);
                const hasSuccess = (importData.inserted > 0 || importData.updated > 0 || (importData.success_count !== undefined && importData.success_count > 0));
                const hasSkipped = importData.skipped && importData.skipped > 0;
                const totalProcessed = importData.total_records || importData.total_rows || 0;
                const totalFailed = importData.error_count || importData.failed || 0;
                
                // Check if API explicitly returned success: false
                const apiSuccessFlag = data.success;
                const isApiFailure = apiSuccessFlag === false;
                
                // Determine modal type and title based on results
                // If API says success: false, don't show "Import Completed Successfully"
                const isCompleteFailure = hasErrors && !hasSuccess && !hasSkipped && totalFailed === totalProcessed;
                let modalType, modalTitle;
                
                if (isApiFailure) {
                    // API explicitly says success: false
                    if (hasSuccess || hasSkipped) {
                        modalType = 'warning';
                        modalTitle = 'Import Completed with Errors';
                    } else {
                        modalType = 'error';
                        modalTitle = 'Import Failed';
                    }
                } else {
                    // API says success: true or no success flag
                    modalType = hasErrors ? (hasSuccess || hasSkipped ? 'warning' : 'error') : 'success';
                    modalTitle = isCompleteFailure 
                        ? 'Import Failed'
                        : hasErrors 
                        ? (hasSuccess || hasSkipped ? 'Import Completed with Errors' : 'Import Failed')
                        : 'Import Completed Successfully';
                }
                
                const ModalComponent = modalType === 'error' ? Modal.error : modalType === 'warning' ? Modal.warning : Modal.success;
                
                // Show detailed import summary
                ModalComponent({
                    title: modalTitle,
                    width: 800,
                    content: (
                        <div style={{ padding: '10px 0' }}>
                            {/* Backend message */}
                            {data.message && (
                                <div style={{ 
                                    marginBottom: '16px', 
                                    fontSize: '16px', 
                                    fontWeight: 'normal',
                                    color: '#262626',
                                    padding: '12px',
                                    background: hasErrors ? '#fffbe6' : '#f6ffed',
                                    border: `1px solid ${hasErrors ? '#ffe58f' : '#b7eb8f'}`,
                                    borderRadius: '4px',
                                    lineHeight: '1.5'
                                }}>
                                    {data.message}
                                </div>
                            )}

                            {/* Statistics Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                                <div style={{ padding: '8px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                                    <strong>Total Rows:</strong> {importData.total_rows || importData.total_records || 0}
                                </div>
                                <div style={{ padding: '8px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                                    <strong>Records Processed:</strong> {importData.total_records || importData.total_rows || 0}
                                </div>
                                {(importData.inserted !== undefined && importData.inserted > 0) && (
                                    <div style={{ padding: '8px', background: '#e6fffb', border: '1px solid #87e8de', borderRadius: '4px' }}>
                                        <strong>Inserted:</strong> {importData.inserted}
                                    </div>
                                )}
                                {(importData.updated !== undefined && importData.updated > 0) && (
                                    <div style={{ padding: '8px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                                        <strong>Updated:</strong> {importData.updated}
                                    </div>
                                )}
                                {(importData.skipped !== undefined && importData.skipped > 0) && (
                                    <div style={{ padding: '8px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px' }}>
                                        <strong>Skipped:</strong> {importData.skipped}
                                    </div>
                                )}
                                {(importData.error_count !== undefined && importData.error_count !== null && importData.error_count > 0) && (
                                    <div style={{ padding: '8px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                                        <strong>Errors:</strong> {importData.error_count}
                                    </div>
                                )}
                                {(importData.failed !== undefined && importData.failed !== null && importData.failed > 0) && (
                                    <div style={{ padding: '8px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                                        <strong>Failed:</strong> {importData.failed}
                                    </div>
                                )}
                            </div>

                            {/* Row Errors with Row Data */}
                            {importData.row_errors && importData.row_errors.length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#ff4d4f' }}>
                                        Row Errors ({importData.row_errors.length}):
                                    </div>
                                    <div style={{
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '4px',
                                        padding: '8px'
                                    }}>
                                        {importData.row_errors.map((rowError, index) => (
                                            <div key={index} style={{
                                                marginBottom: '12px',
                                                padding: '12px',
                                                background: '#fff2f0',
                                                border: '1px solid #ffccc7',
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                                                    Row {rowError.row_number} {rowError.errors && `(${rowError.errors.length} error${rowError.errors.length > 1 ? 's' : ''})`}
                                                </div>
                                                
                                                {/* Row Data */}
                                                {rowError.row_data && (
                                                    <div style={{ 
                                                        marginBottom: '8px', 
                                                        padding: '6px',
                                                        background: '#fafafa',
                                                        border: '1px solid #e8e8e8',
                                                        borderRadius: '4px',
                                                        fontSize: '12px'
                                                    }}>
                                                        <strong style={{ color: '#595959' }}>Row Data:</strong>
                                                        <div style={{ marginTop: '4px', color: '#8c8c8c' }}>
                                                            {Object.entries(rowError.row_data).map(([key, value]) => (
                                                                <div key={key} style={{ marginLeft: '8px' }}>
                                                                    <strong>{key}:</strong> {String(value || 'N/A')}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Errors */}
                                                {rowError.errors && rowError.errors.length > 0 && (
                                                    <div>
                                                        <strong style={{ color: '#ff4d4f', fontSize: '13px' }}>Errors:</strong>
                                                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                                                            {rowError.errors.map((error, errorIndex) => (
                                                                <li key={errorIndex} style={{ color: '#ff4d4f', marginBottom: '4px', fontSize: '12px' }}>
                                                                    <strong>{error.field}:</strong> {error.message}
                                                                    {error.value !== undefined && error.value !== null && (
                                                                        <span style={{ color: '#8c8c8c', marginLeft: '8px' }}>
                                                                            (Value: <em>{String(error.value)}</em>)
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Error Summary (if exists) */}
                            {importData.error_summary && importData.error_summary.total_errors > 0 && (
                                <div style={{ marginTop: '16px', padding: '8px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                        Error Summary:
                                    </div>
                                    <div>Total Errors: {importData.error_summary.total_errors}</div>
                                    {importData.error_summary.rows_with_errors && (
                                        <div>Rows with Errors: {importData.error_summary.rows_with_errors}</div>
                                    )}
                                    {importData.error_summary.error_types && (
                                        <div>Error Types: {Object.entries(importData.error_summary.error_types).map(([type, count]) =>
                                            `${type}: ${count}`).join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ),
                    onOk() {
                        refreshTable?.();
                    },
                    okText: "Continue"
                });
                return;
            }
            
            // Handle other error cases
            let errorMessage = "Failed to import file!";

            if (data?.errors?.length > 0) {
               
                Modal.error({
                    title: "Import Failed",
                    width: 600,
                    content: (
                        <div style={{
                            maxHeight: 300,
                            overflowY: "auto",
                            paddingRight: 10
                        }}>
                            <ul style={{ paddingLeft: 20 }}>
                                {data.errors.map((e, i) => (
                                    <li key={i} style={{ color: "red" }}>{e}</li>
                                ))}
                            </ul>
                        </div>
                    ),
                });
                setLoading(false);
                return;
            }

            if (data) {
                if (typeof data === "string") {
                    errorMessage = data;
                } else if (data.message) {
                    errorMessage = data.message;
                } else if (data.error) {
                    errorMessage = data.error;
                } else if (Array.isArray(data.errors)) {
                    
                    errorMessage = data.errors.join(", ");
                } else {
                   
                    errorMessage = Object.values(data).join(", ");
                }
            }

            message.error(errorMessage);
            setLoading(false);
            onClose();
        }

        setLoading(false);
    };

    return (
        <Modal
            title="Import Data"
            open={open}
            onCancel={onClose}
            afterClose={() => {
                setFile(null);
                setFileList([]);
              }}
            onOk={handleImport}
            okText="Import"
            confirmLoading={loading}
            width={500}
            okButtonProps={{
                disabled: !file, 
            }}
        >
            {formatFilePath && (
                <div style={{ marginBottom: 15 }}>
                    <a
                        href={formatFilePath}
                        download={getCleanFileName(formatFilePath)}
                        style={{
                            background: "#374151",
                            padding: "6px 14px",
                            color: "#fff",
                            borderRadius: 6,
                            textDecoration: "none",
                            fontWeight: 500
                        }}
                    >
                        ⬇ Download Format File
                    </a>
                </div>
            )}
           
            <Dragger {...uploadProps} style={{ padding: 20 }}>
                <p style={{ marginBottom: 10 }}>
                    <UploadOutlined style={{ fontSize: 28, color: "#374151" }} />
                </p>
                <p style={{ fontSize: 16, color: "#777" }}>
                    Drag files to upload
                </p>
            </Dragger>

         
            <div style={{ textAlign: "center", marginTop: 20 }}>
                <Button
                    onClick={() => document.querySelector(".custom-file-upload input").click()}
                    style={{
                        background: "#374151",
                        color: "#fff",
                        padding: "6px 20px",
                        borderRadius: 6,
                        fontWeight: 600,
                    }}
                >
                    Choose File
                </Button>

             
                <div className="custom-file-upload" style={{ display: "none" }}>
                    <Upload {...uploadProps}>
                        <input type="file" />
                    </Upload>
                </div>
            </div>

            {file && (
                <div style={{ marginTop: 15, textAlign: "center", fontWeight: 500 }}>
                    Selected File: {file.name}
                </div>
            )}
        </Modal>
    );
};

export default ImportDataModal;

